import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, HelpCircle, AlertCircle, Link2, Key, Mail, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';

const PERMISSIONS = [
  { id: 'facturas', label: 'Facturas', description: 'Crear y gestionar facturas emitidas' },
  { id: 'gastos', label: 'Gastos', description: 'Registrar facturas recibidas y gastos' },
  { id: 'contactos', label: 'Contactos', description: 'Gestionar clientes y proveedores' },
  { id: 'productos', label: 'Productos', description: 'Gestionar catalogo de productos/servicios' },
];

const SYNC_FREQUENCIES = [
  { value: 'realtime', label: 'Tiempo real', description: 'Sincronizacion instantanea' },
  { value: 'daily', label: 'Diaria', description: 'Una vez al dia' },
  { value: 'weekly', label: 'Semanal', description: 'Una vez por semana' },
];

const FieldHelp = ({ text }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <HelpCircle className="w-4 h-4 text-slate-400 cursor-help" />
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p>{text}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export default function HoldedStep({ initialData, onNext, onBack, saving }) {
  const [formData, setFormData] = useState(() => ({
    holded_access_method: initialData?.holded_access_method || 'invite',
    holded_user_email: initialData?.holded_user_email || '',
    holded_api_key: initialData?.holded_api_key || '',
    holded_permissions_granted: initialData?.holded_permissions_granted || ['facturas', 'gastos', 'contactos'],
    holded_serie_default: initialData?.holded_serie_default || '',
    holded_sync_frequency: initialData?.holded_sync_frequency || 'realtime',
  }));
  const [errors, setErrors] = useState({});
  const [connectionTest, setConnectionTest] = useState({ testing: false, result: null });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
    // Reset connection test when API key changes
    if (field === 'holded_api_key') {
      setConnectionTest({ testing: false, result: null });
    }
  };

  const handlePermissionToggle = (permissionId) => {
    setFormData(prev => {
      const permissions = prev.holded_permissions_granted || [];
      if (permissions.includes(permissionId)) {
        return {
          ...prev,
          holded_permissions_granted: permissions.filter(p => p !== permissionId)
        };
      }
      return {
        ...prev,
        holded_permissions_granted: [...permissions, permissionId]
      };
    });
  };

  const testConnection = async () => {
    if (!formData.holded_api_key) {
      setErrors(prev => ({ ...prev, holded_api_key: 'Introduce la Clave API para probar la conexion' }));
      return;
    }

    setConnectionTest({ testing: true, result: null });

    // Simulate API test (in real implementation, call backend to test Holded API)
    setTimeout(() => {
      // For demo, we'll simulate a successful connection if key is > 20 chars
      if (formData.holded_api_key.length >= 20) {
        setConnectionTest({ testing: false, result: 'success' });
      } else {
        setConnectionTest({ testing: false, result: 'error' });
        setErrors(prev => ({ ...prev, holded_api_key: 'Clave API invalida o sin permisos suficientes' }));
      }
    }, 1500);
  };

  const validate = () => {
    const newErrors = {};

    if (formData.holded_access_method === 'invite') {
      if (!formData.holded_user_email) {
        newErrors.holded_user_email = 'El email es obligatorio para el metodo de invitacion';
      } else if (!/\S+@\S+\.\S+/.test(formData.holded_user_email)) {
        newErrors.holded_user_email = 'Email invalido';
      }
    }

    if (formData.holded_access_method === 'api_key') {
      if (!formData.holded_api_key) {
        newErrors.holded_api_key = 'La Clave API es obligatoria';
      } else if (formData.holded_api_key.length < 20) {
        newErrors.holded_api_key = 'La Clave API parece invalida (muy corta)';
      }
    }

    if (formData.holded_permissions_granted.length === 0) {
      newErrors.permissions = 'Debes seleccionar al menos un permiso';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onNext(formData);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-1">Integracion con Holded</h2>
        <p className="text-slate-600">Conecta tu cuenta de Holded para gestionar facturas y gastos</p>
      </div>

      {/* Info Alert */}
      <Alert className="bg-blue-50 border-blue-200">
        <Link2 className="h-4 w-4 text-blue-600" />
        <AlertTitle className="text-blue-900">Que es Holded?</AlertTitle>
        <AlertDescription className="text-blue-800">
          Holded es el software de facturacion que usamos para emitir tus facturas y gestionar gastos.
          Necesitamos acceso a tu cuenta para poder operar en tu nombre.
        </AlertDescription>
      </Alert>

      {/* Metodo de Acceso */}
      <div className="space-y-4">
        <Label className="text-base font-semibold">Metodo de Acceso <span className="text-red-500">*</span></Label>

        <RadioGroup
          value={formData.holded_access_method}
          onValueChange={(v) => handleChange('holded_access_method', v)}
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          <div className={`
            relative flex cursor-pointer rounded-lg border p-4 transition-all
            ${formData.holded_access_method === 'invite'
              ? 'border-primary bg-primary/5 ring-2 ring-primary'
              : 'border-slate-200 hover:border-slate-300'}
          `}>
            <RadioGroupItem value="invite" id="invite" className="sr-only" />
            <label htmlFor="invite" className="flex items-start gap-3 cursor-pointer w-full">
              <Mail className={`w-6 h-6 mt-0.5 ${formData.holded_access_method === 'invite' ? 'text-primary' : 'text-slate-400'}`} />
              <div>
                <div className="font-medium">Invitacion por Email</div>
                <p className="text-sm text-slate-500 mt-1">
                  Te enviaremos una invitacion para unirte a tu organizacion en Holded
                </p>
              </div>
            </label>
          </div>

          <div className={`
            relative flex cursor-pointer rounded-lg border p-4 transition-all
            ${formData.holded_access_method === 'api_key'
              ? 'border-primary bg-primary/5 ring-2 ring-primary'
              : 'border-slate-200 hover:border-slate-300'}
          `}>
            <RadioGroupItem value="api_key" id="api_key" className="sr-only" />
            <label htmlFor="api_key" className="flex items-start gap-3 cursor-pointer w-full">
              <Key className={`w-6 h-6 mt-0.5 ${formData.holded_access_method === 'api_key' ? 'text-primary' : 'text-slate-400'}`} />
              <div>
                <div className="font-medium">Clave API</div>
                <p className="text-sm text-slate-500 mt-1">
                  Proporciona una Clave API de tu cuenta de Holded (avanzado)
                </p>
              </div>
            </label>
          </div>
        </RadioGroup>
      </div>

      {/* Campos segun metodo */}
      {formData.holded_access_method === 'invite' && (
        <div className="bg-slate-50 rounded-xl p-5 space-y-4">
          <div>
            <Label className="flex items-center gap-2 mb-2">
              Email para Invitacion <span className="text-red-500">*</span>
              <FieldHelp text="Este email recibira la invitacion para acceder a Holded" />
            </Label>
            <Input
              type="email"
              value={formData.holded_user_email}
              onChange={(e) => handleChange('holded_user_email', e.target.value)}
              placeholder="tu@email.com"
              className={errors.holded_user_email ? 'border-red-500' : ''}
              data-testid="input-holded-email"
            />
            {errors.holded_user_email && (
              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" /> {errors.holded_user_email}
              </p>
            )}
          </div>
          <p className="text-sm text-slate-600">
            Despues de completar la incorporacion, recibiras un email de Holded con instrucciones para aceptar la invitacion.
          </p>
        </div>
      )}

      {formData.holded_access_method === 'api_key' && (
        <div className="bg-slate-50 rounded-xl p-5 space-y-4">
          <div>
            <Label className="flex items-center gap-2 mb-2">
              Clave API de Holded <span className="text-red-500">*</span>
              <FieldHelp text="Puedes obtener tu Clave API en Holded > Configuracion > Integraciones > API" />
            </Label>
            <div className="flex gap-2">
              <Input
                type="password"
                value={formData.holded_api_key}
                onChange={(e) => handleChange('holded_api_key', e.target.value)}
                placeholder="hld_xxxxxxxxxxxxxxxxxxxx"
                className={`flex-1 ${errors.holded_api_key ? 'border-red-500' : connectionTest.result === 'success' ? 'border-green-500' : ''}`}
                data-testid="input-holded-api-key"
              />
              <Button
                type="button"
                variant="outline"
                onClick={testConnection}
                disabled={connectionTest.testing}
              >
                {connectionTest.testing ? (
                  <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                ) : connectionTest.result === 'success' ? (
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                ) : connectionTest.result === 'error' ? (
                  <XCircle className="w-4 h-4 text-red-500" />
                ) : (
                  'Probar'
                )}
              </Button>
            </div>
            {errors.holded_api_key && (
              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" /> {errors.holded_api_key}
              </p>
            )}
            {connectionTest.result === 'success' && (
              <p className="text-green-600 text-sm mt-1 flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" /> Conexion exitosa
              </p>
            )}
          </div>

          <Alert className="bg-amber-50 border-amber-200">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800 text-sm">
              La Clave API se almacenara de forma segura y encriptada. Nunca la compartiremos con terceros.
            </AlertDescription>
          </Alert>
        </div>
      )}

      {/* Permisos Requeridos */}
      <div className="space-y-4">
        <Label className="text-base font-semibold flex items-center gap-2">
          Permisos Requeridos <span className="text-red-500">*</span>
          <FieldHelp text="Selecciona los permisos que necesitamos en Holded para gestionar tu cuenta" />
        </Label>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {PERMISSIONS.map(permission => (
            <div
              key={permission.id}
              className={`
                flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all
                ${formData.holded_permissions_granted?.includes(permission.id)
                  ? 'border-primary bg-primary/5'
                  : 'border-slate-200 hover:border-slate-300'}
              `}
              onClick={() => handlePermissionToggle(permission.id)}
            >
              <Checkbox
                checked={formData.holded_permissions_granted?.includes(permission.id)}
                onCheckedChange={() => handlePermissionToggle(permission.id)}
                className="mt-0.5"
              />
              <div>
                <div className="font-medium text-sm">{permission.label}</div>
                <p className="text-xs text-slate-500">{permission.description}</p>
              </div>
            </div>
          ))}
        </div>
        {errors.permissions && (
          <p className="text-red-500 text-sm flex items-center gap-1">
            <AlertCircle className="w-4 h-4" /> {errors.permissions}
          </p>
        )}
      </div>

      {/* Configuracion Adicional */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label className="flex items-center gap-2 mb-2">
            Serie en Holded
            <FieldHelp text="Si la serie en Holded es diferente a la configurada, indicala aqui" />
          </Label>
          <Input
            value={formData.holded_serie_default}
            onChange={(e) => handleChange('holded_serie_default', e.target.value.toUpperCase())}
            placeholder="Dejar vacio si es igual"
            data-testid="input-holded-serie"
          />
        </div>

        <div>
          <Label className="flex items-center gap-2 mb-2">
            Frecuencia de Sincronizacion
          </Label>
          <Select
            value={formData.holded_sync_frequency}
            onValueChange={(v) => handleChange('holded_sync_frequency', v)}
          >
            <SelectTrigger data-testid="select-sync-frequency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SYNC_FREQUENCIES.map(freq => (
                <SelectItem key={freq.value} value={freq.value}>
                  {freq.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-between pt-6 border-t">
        <Button variant="outline" onClick={onBack} data-testid="btn-back">
          <ChevronLeft className="w-4 h-4 mr-2" /> Atras
        </Button>
        <Button onClick={handleSubmit} disabled={saving} data-testid="btn-next">
          {saving ? 'Guardando...' : 'Continuar'} <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
